/**
 * Apps and dashboards on screen, named to the server on each aggregate.
 *
 * `POST /object-types/{ot}/aggregate` asks for the type's view grant, like
 * `GET /records`. A dashboard someone shared with you keeps working even when
 * your profile does not include its object type: the server concedes the type
 * when the request names an open app that you may open, that uses the type and
 * that someone who can read it authored (backend/ontology_service/type_read_gate.py).
 * This module is how the request names it, so no widget has to thread the app
 * id through its fetch.
 *
 * Every mounted AppCanvas registers its app (embedded dashboards too); the
 * header carries the most recent ones. The header is a hint, not a credential:
 * the server checks every condition against the app row.
 */

import { useLayoutEffect } from 'react';

export const APP_IDS_HEADER = 'X-App-Ids';
const MAX_IDS = 4;
const AGGREGATE_PATH = /\/object-types\/[^/]+\/aggregate\/?$/;

// app id -> how many canvases have it mounted, in mount order.
const mounted = new Map<string, number>();

/** Register an open app; returns the cleanup that unregisters it. */
export function enterOpenApp(appId: string | undefined | null): () => void {
  if (!appId) return () => {};
  const n = mounted.get(appId) ?? 0;
  // Re-inserting moves the id to the end: the latest mount is named first.
  mounted.delete(appId);
  mounted.set(appId, n + 1);
  let done = false;
  return () => {
    if (done) return;
    done = true;
    const left = (mounted.get(appId) ?? 1) - 1;
    if (left <= 0) mounted.delete(appId);
    else mounted.set(appId, left);
  };
}

/**
 * Keep `appId` registered while the calling component is mounted.
 *
 * A layout effect, not a passive one: React runs every layout effect of a
 * commit before any passive effect, so the app is registered before a
 * widget's fetch effect fires (a passive effect in the canvas would run after
 * its children's). StrictMode's simulated remount keeps the same order.
 */
export function useOpenApp(appId: string | undefined | null): void {
  useLayoutEffect(() => enterOpenApp(appId), [appId]);
}

/** The header value, latest first; null with no app on screen. */
export function openAppsHeader(): string | null {
  const ids = [...mounted.keys()].reverse().slice(0, MAX_IDS);
  return ids.length ? ids.join(',') : null;
}

export function resetOpenApps(): void {
  mounted.clear();
}

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

/** Adds the open apps to aggregate requests; everything else passes untouched. */
export function makeOpenAppsFetch(transport: typeof fetch): typeof fetch {
  return (input, init) => {
    const value = openAppsHeader();
    if (!value) return transport(input, init);
    let path: string;
    try {
      path = new URL(urlOf(input), 'http://local').pathname;
    } catch {
      return transport(input, init);
    }
    if (!AGGREGATE_PATH.test(path)) return transport(input, init);
    const headers = new Headers(input instanceof Request ? input.headers : undefined);
    new Headers(init?.headers).forEach((v, k) => headers.set(k, v));
    if (!headers.has(APP_IDS_HEADER)) headers.set(APP_IDS_HEADER, value);
    return transport(input, { ...init, headers });
  };
}
